import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { getSubscriptionStatus } from '../services/api';

// Create the context
export const AuthContext = createContext(null);

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [subscription, setSubscription] = useState(null);

  // Fetch subscription status when user changes
  const refreshSubscription = useCallback(async (cognitoSub) => {
    if (!cognitoSub) {
      setSubscription(null);
      return;
    }
    try {
      const status = await getSubscriptionStatus(cognitoSub);
      setSubscription(status);
    } catch (err) {
      // Transient failure (network/timeout/5xx, already reported to Sentry in the
      // service layer). Keep the last-known-good plan instead of downgrading a
      // paying user to the free tier over a momentary blip.
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (session.tokens?.idToken) {
        const payload = session.tokens.idToken.payload;
        const userData = {
          email: payload.email,
          displayName: payload.name || payload.email?.split('@')[0],
          idToken: session.tokens.idToken.toString(),
          sub: payload.sub
        };
        setUser(userData);
        refreshSubscription(payload.sub);
      }
    } catch (err) {
      // Not authenticated - that's ok
      setUser(null);
      setSubscription(null);
    }
    setLoading(false);
  }, [refreshSubscription]);

  // Check for existing session on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleAuthenticated = useCallback((userData) => {
    setUser(userData);
    setShowAuthModal(false);
    refreshSubscription(userData?.sub);
  }, [refreshSubscription]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Sign out error:', err);
    } finally {
      // Always reflect logged-out state locally, even if the remote sign-out failed,
      // so callers (e.g. the re-auth flow) never observe a stale-truthy user — which
      // would otherwise let an auto-resume effect retry against a dead session forever.
      setUser(null);
      setSubscription(null);
    }
  }, []);

  const openAuthModal = useCallback(() => {
    setShowAuthModal(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setShowAuthModal(false);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    showAuthModal,
    subscription,
    refreshSubscription,
    handleAuthenticated,
    handleSignOut,
    openAuthModal,
    closeAuthModal,
    checkAuth
  }), [user, loading, showAuthModal, subscription, refreshSubscription, handleAuthenticated, handleSignOut, openAuthModal, closeAuthModal, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
